import { useState, useEffect, useCallback } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type {
  TemplateDefinition,
  TemplateField,
  TemplateData,
  RepeatableRow,
  OfficerProfile,
} from "./types";
import { getOfficer } from "./storage";

// ── Field Renderer ──

interface FieldProps {
  field: TemplateField;
  value: string;
  onChange: (value: string) => void;
}

function FieldInput({ field, value, onChange }: FieldProps) {
  const prefillValue = usePrefill(field);

  const currentValue = value || prefillValue || "";

  switch (field.type) {
    case "textarea":
      return (
        <textarea
          className="lepc-textarea"
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
        />
      );
    case "number":
      return (
        <input
          type="number"
          className="lepc-input"
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
        />
      );
    case "date":
      return (
        <input
          type="date"
          className="lepc-input"
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "datetime-local":
      return (
        <input
          type="datetime-local"
          className="lepc-input"
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "select":
      return (
        <select
          className="lepc-select"
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Select...</option>
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    case "radio":
      return (
        <div className="lepc-radio-group">
          {field.options?.map((opt) => (
            <label key={opt.value} className="lepc-radio-label">
              <input
                type="radio"
                name={field.id}
                value={opt.value}
                checked={currentValue === opt.value}
                onChange={() => onChange(opt.value)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      );
    case "checkbox":
      return (
        <label className="lepc-checkbox-label">
          <input
            type="checkbox"
            checked={currentValue === "true"}
            onChange={(e) => onChange(e.target.checked ? "true" : "")}
          />
          {field.label}
        </label>
      );
    case "slider":
      return (
        <div style={{ display: "flex", alignItems: "center" }}>
          <input
            type="range"
            className="lepc-slider"
            min={field.min ?? 1}
            max={field.max ?? 10}
            step={field.step ?? 1}
            value={currentValue || String(field.min ?? 1)}
            onChange={(e) => onChange(e.target.value)}
          />
          <span className="lepc-slider-value">{currentValue || field.min}</span>
        </div>
      );
    case "toggle":
      return (
        <div
          className={`lepc-toggle ${currentValue === "true" ? "active" : ""}`}
          onClick={() => onChange(currentValue === "true" ? "" : "true")}
        >
          <div className="lepc-toggle-switch" />
          <span>{currentValue === "true" ? "Yes, I use this" : "Not applicable to my assignment"}</span>
        </div>
      );
    default:
      return (
        <input
          type="text"
          className="lepc-input"
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
        />
      );
  }
}

function usePrefill(field: TemplateField): string {
  const [prefill, setPrefill] = useState("");

  useEffect(() => {
    if (field.prefill) {
      setPrefill(field.prefill);
      return;
    }
    if (field.prefillFrom) {
      const officer = getOfficer();
      if (!officer) return;
      switch (field.prefillFrom) {
        case "name": setPrefill(officer.fullName); break;
        case "department": setPrefill(officer.department); break;
        case "badge": setPrefill(officer.badgeNumber); break;
        case "rank": setPrefill(officer.rank); break;
        case "district": setPrefill(officer.patrolDistrict); break;
      }
    }
  }, [field.prefill, field.prefillFrom]);

  return prefill;
}

// ── Auto-date calculation ──

function useAutoDate(field: TemplateField, allFields: Record<string, string>): string {
  const [autoDate, setAutoDate] = useState("");

  useEffect(() => {
    if (field.autoDateFrom && field.autoDateOffsetMonths) {
      const sourceDate = allFields[field.autoDateFrom];
      if (sourceDate) {
        const d = new Date(sourceDate);
        d.setMonth(d.getMonth() + field.autoDateOffsetMonths);
        setAutoDate(d.toISOString().split("T")[0]);
      }
    }
  }, [field.autoDateFrom, field.autoDateOffsetMonths, allFields]);

  return autoDate;
}

// ── Repeatable Row Group ──

interface RepeatableProps {
  group: NonNullable<TemplateDefinition["sections"][0]["repeatable"]>;
  rows: RepeatableRow[];
  onChange: (rows: RepeatableRow[]) => void;
}

function RepeatableGroup({ group, rows, onChange }: RepeatableProps) {
  const addRow = () => {
    if (group.maxRows && rows.length >= group.maxRows) return;
    const newRow: RepeatableRow = {};
    for (const f of group.fields) newRow[f.id] = "";
    onChange([...rows, newRow]);
  };

  const removeRow = (idx: number) => {
    if (group.minRows && rows.length <= group.minRows) return;
    onChange(rows.filter((_, i) => i !== idx));
  };

  const updateField = (idx: number, fieldId: string, value: string) => {
    const updated = [...rows];
    updated[idx] = { ...updated[idx], [fieldId]: value };
    onChange(updated);
  };

  return (
    <div>
      {rows.map((row, idx) => (
        <div key={idx} className="lepc-repeatable-row">
          <div className="lepc-repeatable-row-header">
            <span className="lepc-repeatable-row-label">
              {group.label} {idx + 1}
            </span>
            {(!group.minRows || rows.length > group.minRows) && (
              <button
                className="lepc-repeatable-remove"
                onClick={() => removeRow(idx)}
              >
                Remove
              </button>
            )}
          </div>
          {group.fields.map((f) => (
            <div key={f.id} className="lepc-field">
              <label className="lepc-field-label">{f.label}</label>
              <FieldInput
                field={f}
                value={row[f.id] ?? ""}
                onChange={(v) => updateField(idx, f.id, v)}
              />
            </div>
          ))}
        </div>
      ))}
      {(!group.maxRows || rows.length < group.maxRows) && (
        <button className="lepc-add-button" onClick={addRow}>
          {group.addLabel ?? "+ Add Row"}
        </button>
      )}
    </div>
  );
}

// ── Checklist Group ──

interface ChecklistProps {
  group: NonNullable<TemplateDefinition["sections"][0]["checklist"]>;
  checked: Record<string, boolean>;
  notes: Record<string, string>;
  onCheckedChange: (checked: Record<string, boolean>) => void;
  onNotesChange: (notes: Record<string, string>) => void;
}

function ChecklistGroup({ group, checked, notes, onCheckedChange, onNotesChange }: ChecklistProps) {
  const toggle = (itemId: string) => {
    onCheckedChange({ ...checked, [itemId]: !checked[itemId] });
  };

  const updateNote = (itemId: string, value: string) => {
    onNotesChange({ ...notes, [itemId]: value });
  };

  return (
    <div>
      {group.items.map((item) => (
        <div key={item.id} className="lepc-checklist-item">
          <label className="lepc-checkbox-label">
            <input
              type="checkbox"
              checked={checked[item.id] ?? false}
              onChange={() => toggle(item.id)}
            />
            {item.label}
          </label>
          {checked[item.id] && (
            <div className="lepc-checklist-notes">
              <textarea
                placeholder="Notes for this item..."
                value={notes[item.id] ?? ""}
                onChange={(e) => updateNote(item.id, e.target.value)}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Collapsible Context Box ──

function ContextBox({ text }: { text: string }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="lepc-context-box">
      <div
        className="lepc-context-box-header"
        onClick={() => setOpen(!open)}
      >
        <span>Architecture Context</span>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </div>
      {open && <div className="lepc-context-box-body">{text}</div>}
    </div>
  );
}

// ── Section Renderer ──

interface SectionProps {
  section: TemplateDefinition["sections"][0];
  data: TemplateData;
  onFieldChange: (fieldId: string, value: string) => void;
  onRepeatableChange: (groupId: string, rows: RepeatableRow[]) => void;
  onChecklistChange: (groupId: string, checked: Record<string, boolean>) => void;
  onChecklistNotesChange: (groupId: string, notes: Record<string, string>) => void;
  stepNumber?: number;
}

function SectionRenderer({
  section,
  data,
  onFieldChange,
  onRepeatableChange,
  onChecklistChange,
  onChecklistNotesChange,
  stepNumber,
}: SectionProps) {
  const allFieldValues: Record<string, string> = {};
  for (const [k, v] of Object.entries(data.fields)) {
    allFieldValues[k] = typeof v === "string" ? v : "";
  }

  // Compute auto-date values
  const autoDates: Record<string, string> = {};
  for (const f of section.fields ?? []) {
    if (f.autoDateFrom && f.autoDateOffsetMonths) {
      const source = allFieldValues[f.autoDateFrom];
      if (source) {
        const d = new Date(source);
        d.setMonth(d.getMonth() + f.autoDateOffsetMonths);
        autoDates[f.id] = d.toISOString().split("T")[0];
      }
    }
  }

  const variantClass = section.variant ?? "default";

  if (variantClass === "steps") {
    return (
      <div className="lepc-step-card">
        <div className="lepc-step-title">
          {stepNumber && <span className="lepc-step-badge">{stepNumber}</span>}
          {section.title.replace(/^Step \d+\s*—\s*/, "")}
        </div>
        {section.fields?.map((f) => (
          <div key={f.id} className="lepc-field" style={{ marginTop: "1rem" }}>
            <label className="lepc-field-label">{f.label}</label>
            <FieldInput
              field={f}
              value={(data.fields[f.id] as string) ?? autoDates[f.id] ?? ""}
              onChange={(v) => onFieldChange(f.id, v)}
            />
          </div>
        ))}
      </div>
    );
  }

  if (variantClass === "matrix") {
    const rows = (data.repeatables[section.repeatable!.id] ?? section.repeatable!.prepopulated ?? []) as RepeatableRow[];
    return (
      <div>
        {section.fields && section.fields.length > 0 && (
          <div style={{ marginBottom: "1rem" }}>
            {section.fields.map((f) => (
              <div key={f.id} className="lepc-field">
                <label className="lepc-field-label">{f.label}</label>
                <FieldInput
                  field={f}
                  value={(data.fields[f.id] as string) ?? ""}
                  onChange={(v) => onFieldChange(f.id, v)}
                />
              </div>
            ))}
          </div>
        )}
        {rows.map((row, idx) => {
          const color = (row.color as string) || "green";
          return (
            <div key={idx} className="lepc-matrix-row" style={{ borderLeftColor: colorVar(color) }}>
              <div>
                <span className={`lepc-matrix-badge ${color}`}>
                  {row.level as string}
                </span>
                {section.repeatable!.fields
                  .filter((f) => !["level", "color"].includes(f.id))
                  .map((f) => (
                    <div key={f.id} className="lepc-field">
                      <label className="lepc-field-label">{f.label}</label>
                      <FieldInput
                        field={f}
                        value={(row[f.id] as string) ?? ""}
                        onChange={(v) => {
                          const updated = [...rows];
                          updated[idx] = { ...updated[idx], [f.id]: v };
                          onRepeatableChange(section.repeatable!.id, updated);
                        }}
                      />
                    </div>
                  ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (variantClass === "color-cards") {
    const colorFields = section.fields ?? [];
    const cardColors = ["white", "yellow", "orange", "red"];
    const fieldGroups: TemplateField[][] = [];
    let currentGroup: TemplateField[] = [];
    let currentColor = "";

    for (const f of colorFields) {
      const match = f.label.match(/^(WHITE|YELLOW|ORANGE|RED)/i);
      if (match) {
        if (currentGroup.length > 0) fieldGroups.push(currentGroup);
        currentGroup = [f];
        currentColor = match[1].toLowerCase();
      } else {
        currentGroup.push(f);
      }
    }
    if (currentGroup.length > 0) fieldGroups.push(currentGroup);

    return (
      <div>
        {fieldGroups.map((group, gi) => (
          <div key={gi} className={`lepc-color-card ${cardColors[gi] ?? ""}`}>
            {group.map((f) => (
              <div key={f.id} className="lepc-field">
                <label className="lepc-field-label">{f.label}</label>
                <FieldInput
                  field={f}
                  value={(data.fields[f.id] as string) ?? ""}
                  onChange={(v) => onFieldChange(f.id, v)}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (variantClass === "pillars") {
    const pillarKey = section.id;
    return (
      <div className={`lepc-pillar-card ${pillarKey}`}>
        <span className={`lepc-pillar-badge ${pillarKey}`}>
          {section.title}
        </span>
        {section.fields?.map((f) => (
          <div key={f.id} className="lepc-field">
            <label className="lepc-field-label">{f.label}</label>
            <FieldInput
              field={f}
              value={(data.fields[f.id] as string) ?? ""}
              onChange={(v) => onFieldChange(f.id, v)}
            />
          </div>
        ))}
      </div>
    );
  }

  if (variantClass === "timeline") {
    return (
      <div>
        <div className="lepc-timeline">
          <div className="lepc-timeline-item">
            <div className="lepc-timeline-dot" />
            <div className="lepc-section-title">{section.title}</div>
            {section.description && (
              <div className="lepc-section-description">{section.description}</div>
            )}
            {section.fields?.map((f) => (
              <div key={f.id} className="lepc-field">
                <label className="lepc-field-label">{f.label}</label>
                <FieldInput
                  field={f}
                  value={(data.fields[f.id] as string) ?? ""}
                  onChange={(v) => onFieldChange(f.id, v)}
                />
              </div>
            ))}
            {section.repeatable && (
              <RepeatableGroup
                group={section.repeatable}
                rows={data.repeatables[section.repeatable.id] ?? section.repeatable.prepopulated ?? []}
                onChange={(rows) => onRepeatableChange(section.repeatable!.id, rows)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default section rendering
  return (
    <div className="lepc-section">
      <div className="lepc-section-title">{section.title}</div>
      {section.description && (
        <div className="lepc-section-description">{section.description}</div>
      )}
      {section.fields?.map((f) => (
        <div key={f.id} className="lepc-field">
          {f.type !== "checkbox" && f.type !== "toggle" && (
            <label className="lepc-field-label">{f.label}</label>
          )}
          <FieldInput
            field={f}
            value={(data.fields[f.id] as string) ?? autoDates[f.id] ?? ""}
            onChange={(v) => onFieldChange(f.id, v)}
          />
        </div>
      ))}
      {section.repeatable && (
        <RepeatableGroup
          group={section.repeatable}
          rows={data.repeatables[section.repeatable.id] ?? section.repeatable.prepopulated ?? []}
          onChange={(rows) => onRepeatableChange(section.repeatable!.id, rows)}
        />
      )}
      {section.checklist && (
        <ChecklistGroup
          group={section.checklist}
          checked={data.checklists[section.checklist.id] ?? {}}
          notes={data.checklistNotes[section.checklist.id] ?? {}}
          onCheckedChange={(c) => onChecklistChange(section.checklist!.id, c)}
          onNotesChange={(n) => onChecklistNotesChange(section.checklist!.id, n)}
        />
      )}
    </div>
  );
}

function colorVar(color: string): string {
  const map: Record<string, string> = {
    green: "#2D7D46",
    yellow: "#CC7A00",
    orange: "#d65800",
    red: "#CC0000",
  };
  return map[color] ?? "#D1D5DB";
}

// ── Main Template Renderer ──

interface TemplateRendererProps {
  template: TemplateDefinition;
  data: TemplateData;
  onDataChange: (data: TemplateData) => void;
}

export function TemplateRenderer({ template, data, onDataChange }: TemplateRendererProps) {
  const handleFieldChange = useCallback((fieldId: string, value: string) => {
    onDataChange({
      ...data,
      fields: { ...data.fields, [fieldId]: value },
      status: data.status === "complete" ? "complete" : "in_progress",
      lastSaved: new Date().toISOString(),
    });
  }, [data, onDataChange]);

  const handleRepeatableChange = useCallback((groupId: string, rows: RepeatableRow[]) => {
    onDataChange({
      ...data,
      repeatables: { ...data.repeatables, [groupId]: rows },
      status: data.status === "complete" ? "complete" : "in_progress",
      lastSaved: new Date().toISOString(),
    });
  }, [data, onDataChange]);

  const handleChecklistChange = useCallback((groupId: string, checked: Record<string, boolean>) => {
    onDataChange({
      ...data,
      checklists: { ...data.checklists, [groupId]: checked },
      status: data.status === "complete" ? "complete" : "in_progress",
      lastSaved: new Date().toISOString(),
    });
  }, [data, onDataChange]);

  const handleChecklistNotesChange = useCallback((groupId: string, notes: Record<string, string>) => {
    onDataChange({
      ...data,
      checklistNotes: { ...data.checklistNotes, [groupId]: notes },
      status: data.status === "complete" ? "complete" : "in_progress",
      lastSaved: new Date().toISOString(),
    });
  }, [data, onDataChange]);

  let stepCounter = 0;

  return (
    <div>
      {template.sections.map((section) => {
        if (section.variant === "steps") {
          stepCounter++;
          return (
            <SectionRenderer
              key={section.id}
              section={section}
              data={data}
              onFieldChange={handleFieldChange}
              onRepeatableChange={handleRepeatableChange}
              onChecklistChange={handleChecklistChange}
              onChecklistNotesChange={handleChecklistNotesChange}
              stepNumber={stepCounter}
            />
          );
        }
        return (
          <SectionRenderer
            key={section.id}
            section={section}
            data={data}
            onFieldChange={handleFieldChange}
            onRepeatableChange={handleRepeatableChange}
            onChecklistChange={handleChecklistChange}
            onChecklistNotesChange={handleChecklistNotesChange}
          />
        );
      })}
    </div>
  );
}

export { ContextBox };
export type { OfficerProfile };
